import api from './api';

export interface StatisticsData {
  total_visits: number;
  default_redirects: number;
  intercept_redirects: number;
  success_rate: number;
  realtime_visits: number;
  top_links: Array<{
    id: number;
    title: string;
    short_code?: string;
    total_visits: number;
    default_redirects: number;
    intercept_redirects: number;
    success_rate: number;
  }>;

  geography_distribution: Array<{
    country: string;
    visits: number;
    percentage: number;
  }>;
  device_distribution: Array<{
    device_type: string;
    visits: number;
    percentage: number;
  }>;
  network_distribution: Array<{
    network_type: string;
    visits: number;
    percentage: number;
  }>;

}

export interface LinkStats {
  link_id: number;
  title: string;
  total_visits: number;
  default_redirects: number;
  intercept_redirects: number;
  success_rate: number;
  daily_stats: Array<{
    date: string;
    visits: number;
    default_redirects: number;
    intercept_redirects: number;
  }>;
}

export interface RedirectRatioData {
  total_visits: number;
  default_redirects: number;
  intercept_redirects: number;
  default_percentage: number;
  intercept_percentage: number;
  daily_ratios: Array<{
    date: string;
    default_ratio: number;
    intercept_ratio: number;
  }>;
}



export interface DashboardData {
  today: {
    total_visits: number;
    default_redirects: number;
    intercept_redirects: number;
    success_rate: number;
    realtime_visits: number;
  };
  yesterday: {
    total_visits: number;
    default_redirects: number;
    intercept_redirects: number;
    success_rate: number;
    realtime_visits: number;
  };

}

export interface GeographyStats {
  country_stats: Array<{
    country_code: string;
    country_name: string;
    count: number;
  }>;
  city_stats: Array<{
    city: string;
    count: number;
  }>;
}

export interface DeviceStats {
  device_type_stats: Array<{
    device_type: string;
    count: number;
  }>;
  device_brand_stats: Array<{
    device_brand: string;
    count: number;
  }>;
  os_stats: Array<{
    os_type: string;
    count: number;
  }>;
  browser_stats: Array<{
    browser_type: string;
    count: number;
  }>;
}

// 统计分析API服务
export const statisticsService = {
  // 获取概览数据
  getOverview: async (
    linkId?: number, 
    startDate?: string, 
    endDate?: string,
    clientId?: number,
    shortCode?: string
  ): Promise<StatisticsData> => {
    const params: Record<string, any> = { 
      link_id: linkId,
      start_date: startDate,
      end_date: endDate,
    };
    
    if (clientId) {
      params.client_id = clientId;
    }
    if (shortCode) {
      params.short_code = shortCode;
    }
    
    const response = await api.post('/api/stats/overview', params);
    const responseData = response.data.data;
    
    // 转换后端数据结构为前端期望的格式
    return {
      total_visits: responseData.total?.visitors || 0,
      default_redirects: responseData.total?.normal || 0,
      intercept_redirects: responseData.total?.intercepted || 0,
      success_rate: responseData.total?.success_rate || 0,
      realtime_visits: responseData.today?.visitors || 0,
      top_links: (responseData.top_links || []).map((link: any) => ({
        id: link.id,
        title: link.title,
        short_code: link.short_code,
        total_visits: link.total_visits,
        default_redirects: link.default_redirects,
        intercept_redirects: link.intercept_redirects,
        success_rate: link.success_rate,
      })),
      rule_effectiveness: [], // 后端暂未提供，设为空数组
      geography_distribution: [], // 将通过单独接口获取
      device_distribution: [], // 将通过单独接口获取
      network_distribution: [], // 后端暂未提供，设为空数组
    };
  },

  // 获取仪表板数据（今日、昨日、最近7天）
  getDashboard: async (): Promise<DashboardData> => {
    const response = await api.post('/api/stats/dashboard');
    const responseData = response.data.data;
    
    // 转换后端数据结构为前端期望的格式
    return {
      today: {
        total_visits: responseData.today?.visitors || 0,
        default_redirects: responseData.today?.normal || 0,
        intercept_redirects: responseData.today?.intercepted || 0,
        success_rate: responseData.today?.success_rate || 0,
        realtime_visits: responseData.today?.visitors || 0,
      },
      yesterday: {
        total_visits: responseData.yesterday?.visitors || 0,
        default_redirects: responseData.yesterday?.normal || 0,
        intercept_redirects: responseData.yesterday?.intercepted || 0,
        success_rate: responseData.yesterday?.success_rate || 0,
        realtime_visits: 0,
      },

    };
  },

  // 获取链接统计数据
  getLinkStats: async (linkId?: number, startDate?: string, endDate?: string): Promise<LinkStats> => {
    const response = await api.post('/api/stats/links', { 
      link_id: linkId,
      start_date: startDate,
      end_date: endDate
    });
    const responseData = response.data.data;
    
    // 转换后端数据结构为前端期望的格式
    return {
      link_id: linkId || 0,
      title: '', // 需要从链接服务获取
      total_visits: responseData.total_visitors || 0,
      default_redirects: responseData.normal_visitors || 0,
      intercept_redirects: responseData.intercepted_visitors || 0,
      success_rate: responseData.total_visitors > 0 ? 
        (responseData.normal_visitors / responseData.total_visitors) * 100 : 0,
      daily_stats: (responseData.daily_stats || []).map((item: any) => ({
        date: item.date,
        visits: item.total,
        default_redirects: item.normal,
        intercept_redirects: item.intercepted,
      })),
    };
  },

  // 获取跳转比例统计
  getRedirectRatio: async (): Promise<RedirectRatioData> => {
    const response = await api.post('/api/stats/redirect-ratio');
    return response.data.data;
  },



  // 获取访客统计
  getVisitorStats: async (params?: any): Promise<any> => {
    const response = await api.post('/api/stats/visitors', params);
    return response.data.data;
  },

  // 获取地理位置统计
  getGeographyStats: async (params?: any): Promise<GeographyStats> => {
    const response = await api.post('/api/stats/geography', params);
    return response.data.data;
  },

  // 获取设备统计
  getDeviceStats: async (params?: any): Promise<DeviceStats> => {
    const response = await api.post('/api/stats/devices', params);
    return response.data.data;
  },

  // 获取爬虫统计
  getCrawlerStats: async (params?: any): Promise<any> => {
    const response = await api.post('/api/stats/crawlers', params);
    return response.data.data;
  },

  // 获取拦截统计
  getInterceptStats: async (params?: any): Promise<any> => {
    const response = await api.post('/api/stats/intercepts', params);
    return response.data.data;
  },

  // 获取实时统计
  getRealtimeStats: async (): Promise<any> => {
    const response = await api.post('/api/stats/realtime');
    return response.data.data;
  },



  // 获取访问成功率
  getSuccessRate: async (params?: any): Promise<any> => {
    const response = await api.post('/api/stats/success-rate', params);
    return response.data.data;
  },

  // 导出统计数据
  exportStats: async (params?: any): Promise<void> => {
    await api.post('/api/stats/export', params);
  },

  // 获取完整的统计数据（包括地理位置、设备等）
  getCompleteStats: async (
    linkId?: number, 
    startDate?: string, 
    endDate?: string,
    clientId?: number,
    shortCode?: string
  ): Promise<StatisticsData> => {
    try {
      // 构建参数对象
      const params: Record<string, any> = {
        link_id: linkId,
        start_date: startDate,
        end_date: endDate,
      };
      
      // 添加客户ID和短链码参数
      if (clientId) {
        params.client_id = clientId;
      }
      if (shortCode) {
        params.short_code = shortCode;
      }
      
      // 并行获取所有统计数据
      const [overview, geography, devices] = await Promise.all([
        statisticsService.getOverview(linkId, startDate, endDate, clientId, shortCode),
        statisticsService.getGeographyStats(params),
        statisticsService.getDeviceStats(params),
      ]);

      // 转换地理位置数据
      const geographyDistribution = (geography.country_stats || []).map((item: any) => ({
        country: item.country_name || item.country_code,
        visits: item.count,
        percentage: 0, // 需要计算总访问量
      }));

      // 计算地理位置百分比
      const totalGeoVisits = geographyDistribution.reduce((sum, item) => sum + item.visits, 0);
      geographyDistribution.forEach(item => {
        item.percentage = totalGeoVisits > 0 ? (item.visits / totalGeoVisits) * 100 : 0;
      });

      // 转换设备数据
      const deviceDistribution = (devices.device_type_stats || []).map((item: any) => ({
        device_type: item.device_type,
        visits: item.count,
        percentage: 0, // 需要计算总访问量
      }));

      // 计算设备百分比
      const totalDeviceVisits = deviceDistribution.reduce((sum, item) => sum + item.visits, 0);
      deviceDistribution.forEach(item => {
        item.percentage = totalDeviceVisits > 0 ? (item.visits / totalDeviceVisits) * 100 : 0;
      });

      // 使用概览数据中的top_links，它已经包含了所有链接的统计数据
      return {
        ...overview,
        geography_distribution: geographyDistribution,
        device_distribution: deviceDistribution,
        // top_links已经在overview中包含了，不需要额外处理
      };
    } catch (error) {
      console.error('获取完整统计数据失败:', error);
      // 返回基础数据
      return await statisticsService.getOverview();
    }
  },

  // 获取服务使用量统计（IPCore和指纹识别）
  getServiceUsageStats: async (params?: {
    start_date?: string;
    end_date?: string;
    client_ids?: number[];
  }): Promise<ServiceUsageStats> => {
    const response = await api.post('/api/stats/service-usage', params || {});
    return response.data.data;
  },

  // 获取拦截原因统计（必须携带short_code参数）
  getInterceptReasons: async (params: {
    short_code: string;
    start_date?: string;
    end_date?: string;
  }): Promise<InterceptReasonsStats> => {
    const response = await api.post('/api/stats/intercept-reasons', params);
    return response.data.data;
  },
};

// 服务使用量统计数据结构
export interface ServiceUsageStats {
  summary: {
    total_requests: number;
    ipcore_usage: number;
    fingerprint_usage: number;
    start_date: string;
    end_date: string;
  };
  daily: Array<{
    date: string;
    total_requests: number;
    ipcore_usage: number;
    fingerprint_usage: number;
  }>;
  clients?: Array<{
    client_id: number;
    username: string;
    total_requests: number;
    ipcore_usage: number;
    fingerprint_usage: number;
  }>;
}

// 拦截原因统计数据结构
export interface InterceptReason {
  reason: string;
  type: string;
  count: number;
  percentage: number;
}

export interface InterceptReasonsStats {
  short_code: string;
  start_date: string;
  end_date: string;
  total_visits: number;
  normal_visits: number;
  intercepted_visits: number;
  intercept_reasons: InterceptReason[];
} 